/**
 * subscription.mapper — the ONLY place a subscription wire DTO becomes a domain
 * value. Pure functions (no Angular, no HTTP, no storage), so the store, the
 * repository and unit tests can all share them.
 *
 * Two rules run through everything here:
 *  · TOLERANT READS — the live API and the written spec disagree on several key
 *    names (plan `name` vs `name_ar`/`name_en`; checkout `url` vs
 *    `tap_payment_url`; quote totals under `total`/`due`/`payable`). Every
 *    reader takes the first present spelling so one frontend serves both.
 *  · NO INVENTION — a field the server didn't send maps to `null`/`0`, never to
 *    a flattering guess. Premium is granted by the server, never by the mapper.
 */

import {
  CardUpdateCheckout,
  CheckoutOutcome,
  InvoiceStatus,
  PaymentCardOnFile,
  PaymentMethodKind,
  PlanRef,
  SubscriptionCard,
  SubscriptionCatalog,
  SubscriptionCycle,
  SubscriptionInvoice,
  SubscriptionPlan,
  SubscriptionPolicy,
  OrgGrant,
  SubscriptionQuote,
  SubscriptionSnapshot,
  SubscriptionState,
  SubscriptionTier,
  SubscriptionUsage,
} from '@domains/subscription/domain/models/subscription.model';
import { AutoRenewResultDto } from '../api/dtos/responses/auto-renew-result-dto-response';
import { CancelResultDto } from '../api/dtos/responses/cancel-result-dto-response';
import { CardDto } from '../api/dtos/responses/card-dto-response';
import { InvoiceDto } from '../api/dtos/responses/invoice-dto-response';
import { PaymentMethodResultDto } from '../api/dtos/responses/payment-method-result-dto-response';
import { QuoteResultDto } from '../api/dtos/responses/quote-result-dto-response';
import { SubscribeResultDto } from '../api/dtos/responses/subscribe-result-dto-response';
import {
  PaymentMethodInfoDto,
  PremiumWindowDto,
  SubscriptionDto,
  SubscriptionUsageTodayDto,
} from '../api/dtos/responses/subscription-dto-response';
import { SubscriptionPlanDto, SubscriptionPlansDto } from '../api/dtos/responses/subscription-plan-dto-response';
import { SubscriptionPolicyDto } from '../api/dtos/responses/subscription-policy-dto-response';
import { SubscriptionStateDto } from '../api/dtos/responses/subscription-state-dto-response';
import { TrialResultDto } from '../api/dtos/responses/trial-result-dto-response';
import { ToolsCatalogDto } from '../api/dtos/responses/tools-catalog-dto-response';

// ─── primitive readers ────────────────────────────────────────────────────

/** First non-empty string among the candidates, else null. */
function firstText(...values: readonly (string | null | undefined)[]): string | null {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return null;
}

/** Numeric coercion that also accepts the server's stringified money ("9.00"). */
function num(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** First finite number among the candidates, else `fallback`. */
function firstNum(fallback: number, ...values: readonly (number | string | null | undefined)[]): number {
  for (const value of values) {
    const n = num(value);
    if (n != null) return n;
  }
  return fallback;
}

/** ISO 8601 (or epoch-ms) → epoch ms; 0 when absent/unparseable. */
export function toEpochMs(value: string | number | null | undefined): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/** `monthly` unless the server explicitly says `annual`. */
export function toCycle(value: string | null | undefined): SubscriptionCycle {
  return value === 'annual' ? 'annual' : 'monthly';
}

/** Cycle when the server states one, else null (don't presume a cadence). */
function toCycleOrNull(value: string | null | undefined): SubscriptionCycle | null {
  if (value === 'annual') return 'annual';
  if (value === 'monthly') return 'monthly';
  return null;
}

// ─── state / tier ─────────────────────────────────────────────────────────

/** Map a state to the v2 tier (trial_active → trial; premium_active/cancelled_grace → premium). */
export function tierForState(state: SubscriptionStateDto | null | undefined): SubscriptionTier {
  if (state === 'trial_active') return 'trial';
  if (state === 'premium_active' || state === 'cancelled_grace') return 'premium';
  return 'free';
}

const KNOWN_METHODS: readonly PaymentMethodKind[] = ['card', 'wallet', 'apple_pay', 'mada', 'stc_pay'];

/**
 * Normalize the settlement method; an unrecognised value degrades to `card`. The
 * live resource reports the card on file as an object (`{ brand, last4 }`) — that
 * is a card, and an empty one is no method at all.
 */
export function toPaymentMethod(value: PaymentMethodInfoDto | string | null | undefined): PaymentMethodKind | null {
  if (value != null && typeof value === 'object') return toPaymentCard(value) ? 'card' : null;
  const raw = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!raw) return null;
  return (KNOWN_METHODS as readonly string[]).includes(raw) ? (raw as PaymentMethodKind) : 'card';
}

/** `payment_method: { brand, last4 }` → the card on file; null for the keyword shape or an empty object. */
export function toPaymentCard(value: PaymentMethodInfoDto | string | null | undefined): PaymentCardOnFile | null {
  if (value == null || typeof value !== 'object') return null;
  const brand = firstText(value.brand);
  const last4 = firstText(value.last4 != null ? String(value.last4) : null);
  return brand || last4 ? { brand, last4 } : null;
}

/**
 * The server countdown (`premium_ends_at` / `days_left`), carrying ONLY the keys
 * the payload actually has. An absent key stays absent (`undefined`) — that means
 * "older backend, derive it", while an explicit `null` means "not premium, hide".
 * Mutation patches rely on the same distinction: {@link mergeSnapshot} drops
 * `undefined`, so an echo without the keys never wipes a known countdown.
 */
function toPremiumWindow(dto: PremiumWindowDto | null | undefined): Pick<SubscriptionSnapshot, 'premiumEndsAt' | 'daysLeft'> {
  const window: Pick<SubscriptionSnapshot, 'premiumEndsAt' | 'daysLeft'> = {};
  if (!dto) return window;
  if ('premium_ends_at' in dto) window.premiumEndsAt = toEpochMs(dto.premium_ends_at);
  if ('days_left' in dto) {
    const days = num(dto.days_left);
    window.daysLeft = days == null ? null : Math.max(0, Math.floor(days));
  }
  return window;
}

// ─── policy / usage / plan ────────────────────────────────────────────────

/** DTO policy (snake_case) → domain {@link SubscriptionPolicy}; null when absent. */
export function toPolicy(dto: SubscriptionPolicyDto | null | undefined): SubscriptionPolicy | null {
  if (!dto) return null;
  return {
    messageLimit: firstNum(-1, dto.message_limit),
    voiceLimitWeeklyMin: firstNum(-1, dto.voice_limit_weekly_min),
    memoryEnabled: !!dto.memory_enabled,
    proactiveCapDaily: firstNum(0, dto.proactive_cap_daily),
    memoryDays: num(dto.memory_days),
    groupSessionPrice: num(dto.group_session_price),
    priorityReply: typeof dto.priority_reply === 'boolean' ? dto.priority_reply : null,
  };
}

/** DTO usage_today → domain {@link SubscriptionUsage}; null when absent. */
export function toUsage(dto: SubscriptionUsageTodayDto | null | undefined): SubscriptionUsage | null {
  if (!dto) return null;
  return { messagesSent: firstNum(0, dto.messages_sent), voiceSeconds: firstNum(0, dto.voice_seconds) };
}

/**
 * Plan title. The live catalog sends ONE `name` already switched by
 * `Accept-Language`; the documented `name_ar`/`name_en` pair is honoured as a
 * fallback (Arabic first, matching the app's default voice).
 */
export function toPlanName(dto: SubscriptionPlanDto | null | undefined): string {
  return firstText(dto?.name, dto?.title, dto?.name_ar, dto?.name_en) ?? '';
}

/** DTO plan → domain {@link PlanRef}; null when absent or unidentifiable. */
export function toPlanRef(dto: SubscriptionPlanDto | null | undefined): PlanRef | null {
  if (!dto) return null;
  const id = num(dto.id);
  if (id == null) return null;
  return { id, name: toPlanName(dto), price: firstNum(0, dto.price), cycle: toCycle(dto.cycle) };
}

/** One catalog row → domain {@link SubscriptionPlan} (plan + its limits). */
export function toPlan(dto: SubscriptionPlanDto | null | undefined): SubscriptionPlan | null {
  const ref = toPlanRef(dto);
  return ref ? { ...ref, policy: toPolicy(dto?.policy) } : null;
}

/** `GET /plans` → the subscribable catalog, cheapest cadence first (monthly, annual). */
export function toPlans(list: readonly SubscriptionPlanDto[] | null | undefined): SubscriptionPlan[] {
  if (!Array.isArray(list)) return [];
  const plans = list.map(toPlan).filter((p): p is SubscriptionPlan => p !== null);
  // Stable, predictable order for the picker: monthly before annual, then by id.
  return plans.sort((a, b) =>
    a.cycle === b.cycle ? a.id - b.id : a.cycle === 'monthly' ? -1 : 1,
  );
}

/** `GET /plans` in either shape (bare array today, `{ free_policy, plans }` after GAP 01) → catalog. */
export function toCatalog(dto: SubscriptionPlansDto | null | undefined): SubscriptionCatalog {
  if (Array.isArray(dto)) return { plans: toPlans(dto), freePolicy: null };
  return { plans: toPlans(dto?.plans), freePolicy: toPolicy(dto?.free_policy) };
}

/**
 * `GET /tools` → how many distinct tools the catalog holds (a tool listed under
 * two categories counts once, as on the Khoyek tools screen); null when empty.
 */
export function toToolsCount(data: ToolsCatalogDto | null | undefined): number | null {
  const keys = new Set<string>();
  for (const list of Object.values(data?.tools ?? {})) {
    if (!Array.isArray(list)) continue;
    for (const tool of list) if (tool?.key) keys.add(tool.key);
  }
  return keys.size || null;
}

// ─── subscription snapshot ────────────────────────────────────────────────

/** Was a trial already consumed? (§8.3 — a `trial_ends_at` exists on the subscription). */
function trialConsumed(dto: SubscriptionDto): boolean {
  if (dto.has_used_trial === true || dto.trial_used === true) return true;
  return dto.state === 'trial_active' || dto.trial_ends_at != null;
}

/** Full `GET /subscription` DTO → snapshot (carries the raw state + policy/usage/plan). */
export function toSnapshot(dto: SubscriptionDto): SubscriptionSnapshot {
  const tier = tierForState(dto.state);
  const inGrace = dto.state === 'cancelled_grace';
  const plan = toPlanRef(dto.plan);
  return {
    tier,
    trialExpiresAt: tier === 'trial' ? toEpochMs(dto.trial_ends_at) : 0,
    trialDays: num(dto.trial_days),
    // For premium (incl. cancelled_grace) the "next bill" is the period end, or
    // the grace end while cancelled.
    nextBillAt: tier === 'premium' ? toEpochMs(dto.current_period_ends_at) || toEpochMs(dto.grace_ends_at) : 0,
    state: (dto.state ?? null) as SubscriptionState | null,
    graceEndsAt: inGrace ? toEpochMs(dto.grace_ends_at) : 0,
    hadTrial: trialConsumed(dto),
    plan,
    policy: toPolicy(dto.policy),
    usageToday: toUsage(dto.usage_today),
    cycle: toCycleOrNull(dto.cycle) ?? plan?.cycle ?? null,
    autoRenew: typeof dto.auto_renew === 'boolean' ? dto.auto_renew : null,
    mandateOnFile: typeof dto.mandate_on_file === 'boolean' ? dto.mandate_on_file : null,
    paymentMethod: toPaymentMethod(dto.payment_method),
    paymentCard: toPaymentCard(dto.payment_method),
    cardId: dto.card_id != null ? String(dto.card_id) : null,
    orgGrant: toOrgGrant(dto.org_grant),
    ...toPremiumWindow(dto),
  };
}

/**
 * `org_grant` → {@link OrgGrant} (B2B §9.5). `null` for everyone else, which is
 * the ordinary case.
 *
 * `endsAt: null` is preserved as null rather than coerced to 0/now, because for
 * this block null MEANS OPEN-ENDED — a university covering a student for their
 * whole enrolment — and treating it as "expired" would switch the commerce
 * surfaces back on for exactly the members who must not see them.
 */
function toOrgGrant(dto: SubscriptionDto['org_grant']): OrgGrant | null {
  if (!dto) return null;
  const active = dto.active === true;
  return {
    active,
    label: nonEmpty(dto.label),
    organization: nonEmpty(dto.organization),
    endsAt: nonEmpty(dto.ends_at),
    daysLeft: typeof dto.days_left === 'number' && Number.isFinite(dto.days_left) ? dto.days_left : null,
    // THE flag every commerce surface reads. Defaults to the grant's own
    // `active` when the server omitted it, so a live grant never leaves an
    // upgrade CTA showing.
    hidePurchase: typeof dto.hide_purchase === 'boolean' ? dto.hide_purchase : active,
    note: nonEmpty(dto.note),
  };
}

function nonEmpty(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Partial `trial` result → snapshot. Trial mutations echo only what changed, so
 * the caller MERGES this onto the snapshot it already holds rather than
 * replacing it (see {@link mergeSnapshot}) — otherwise a successful trial would
 * blank the plan/usage the last full read established.
 */
export function trialToSnapshot(res: TrialResultDto): Partial<SubscriptionSnapshot> {
  const tier = tierForState(res.state);
  return {
    tier,
    trialExpiresAt: tier === 'trial' ? toEpochMs(res.trial_ends_at) : 0,
    nextBillAt: 0,
    state: (res.state ?? null) as SubscriptionState | null,
    graceEndsAt: 0,
    hadTrial: true,
    policy: toPolicy(res.policy) ?? undefined,
    // `days_left: 7` right after the trial starts — no extra GET needed for the badge.
    ...toPremiumWindow(res),
  };
}

/** Partial `cancel` result → snapshot patch (cancelled_grace keeps premium access). */
export function cancelToSnapshot(res: CancelResultDto): Partial<SubscriptionSnapshot> {
  const tier = tierForState(res.state);
  const graceEndsAt = res.state === 'cancelled_grace' ? toEpochMs(res.grace_ends_at) : 0;
  return {
    tier,
    trialExpiresAt: 0,
    nextBillAt: tier === 'premium' ? graceEndsAt : 0,
    state: (res.state ?? null) as SubscriptionState | null,
    graceEndsAt,
    hadTrial: true,
    // A cancelled subscription no longer auto-renews, whatever it did before.
    autoRenew: false,
    // The countdown becomes the grace countdown.
    ...toPremiumWindow(res),
  };
}

/**
 * `POST /auto-renew` → snapshot patch.
 *
 * The server returns the COMPLETE subscription object (verified 2026-08-04), so when it
 * looks complete we run the FULL snapshot mapper and get period/plan/usage/payment-method
 * refreshed from the same round trip. That is why `state` is the completeness probe: a
 * bare echo has no `state`, and mapping one through `toSnapshot` would blank the plan and
 * usage the last full read established.
 *
 * `autoRenew` is then pinned explicitly: the flag is reported as `auto_renew`, tolerated
 * as `enabled`, and finally falls back to what we REQUESTED — a 200 means the server
 * accepted the flip, so the switch must not spring back just because the payload spelled
 * the field differently.
 */
export function autoRenewToSnapshot(res: AutoRenewResultDto, requested: boolean): Partial<SubscriptionSnapshot> {
  const echoed =
    typeof res?.auto_renew === 'boolean' ? res.auto_renew : typeof res?.enabled === 'boolean' ? res.enabled : null;

  if (res?.state) {
    return { ...toSnapshot(res), autoRenew: echoed ?? requested };
  }

  const patch: Partial<SubscriptionSnapshot> = { autoRenew: echoed ?? requested };
  return patch;
}

/**
 * `POST /payment-method` → the card-update checkout (SUBSCRIPTION-RECURRING R3).
 * `status: pending_payment` is NOT a changed card: the new mandate is stored only
 * when the verification charge captures, so this maps to "where to go", never to
 * a snapshot patch.
 */
export function toCardUpdateCheckout(res: PaymentMethodResultDto | null | undefined): CardUpdateCheckout {
  return {
    url: firstText(res?.tap_payment_url, res?.url),
    message: firstText(res?.message),
    amountDue: num(res?.amount_due),
    currency: firstText(res?.currency) ?? 'SAR',
    willRefund: res?.will_refund === true,
  };
}

/**
 * Apply a partial mutation result onto the snapshot already held. `undefined`
 * entries are dropped so a partial echo never erases a known field, while an
 * explicit `null` (e.g. "no plan any more") still lands.
 */
export function mergeSnapshot(
  base: SubscriptionSnapshot,
  patch: Partial<SubscriptionSnapshot>,
): SubscriptionSnapshot {
  const next = { ...base };
  // TS can't correlate a `keyof T` loop variable's key with its value across a
  // generic index write (microsoft/TypeScript#31445) — this is the one place that
  // needs an assertion, narrowed to `SubscriptionSnapshot`'s own keys (unlike the
  // former whole-object `Record<string, unknown>` round-trip, an unknown key can no
  // longer be written here, and the function's return stays a plain `SubscriptionSnapshot`
  // with no cast needed).
  const writable = next as Record<keyof SubscriptionSnapshot, unknown>;
  for (const key of Object.keys(patch) as (keyof SubscriptionSnapshot)[]) {
    const value = patch[key];
    if (value !== undefined) writable[key] = value;
  }
  return next;
}

// ─── checkout ─────────────────────────────────────────────────────────────

/**
 * Where `POST /subscribe|/reactivate` wants the user to go next.
 *
 * A URL under ANY of the known keys → `redirect`. No URL but a premium state in
 * the echo → the charge settled inline (wallet), so `activated`. Anything else →
 * `pending`: accepted, activation arrives via the webhook, and the UI says so
 * instead of pretending the user is premium.
 *
 * The wallet/promo path returns the resource under `data.subscription`, so that
 * is read as well as the top level. A payment still owed (`pending_payment` /
 * `payment_required`) is never "activated", whatever else the echo says — an
 * already-premium user renewing is premium BEFORE the new charge lands.
 */
export function toCheckoutOutcome(res: SubscribeResultDto | null | undefined): CheckoutOutcome {
  const url = firstText(res?.url, res?.tap_payment_url, res?.payment_url, res?.checkout_url, res?.redirect_url);
  if (url) return { kind: 'redirect', url };
  if (res?.status === 'pending_payment' || res?.payment_required === true) return { kind: 'pending' };
  const sub = res?.subscription;
  const premium =
    res?.state === 'premium_active' ||
    res?.is_premium === true ||
    sub?.state === 'premium_active' ||
    sub?.is_premium === true;
  return premium ? { kind: 'activated' } : { kind: 'pending' };
}

/** `POST /quote` → the money breakdown. Sums are re-derived so they always add up. */
export function toQuote(
  res: QuoteResultDto | null | undefined,
  planId: number,
  cycle: SubscriptionCycle,
  fallbackPrice: number,
): SubscriptionQuote {
  const subtotal = firstNum(fallbackPrice, res?.subtotal, res?.price, res?.amount);
  const discount = Math.max(0, firstNum(0, res?.discount, res?.promo_discount));
  const walletApplied = Math.max(0, firstNum(0, res?.wallet_applied, res?.wallet_amount, res?.wallet_used));
  // Trust the server's total when it sends one; otherwise derive it, clamped at 0.
  const stated = num(res?.total ?? res?.due ?? res?.payable);
  const total = Math.max(0, stated ?? subtotal - discount - walletApplied);
  return {
    planId: firstNum(planId, res?.plan_id),
    cycle: toCycleOrNull(res?.cycle) ?? cycle,
    currency: firstText(res?.currency) ?? 'SAR',
    subtotal,
    discount,
    walletApplied,
    total,
    promoCode: firstText(res?.promo_code),
    promoValid: typeof res?.promo_valid === 'boolean' ? res.promo_valid : null,
    promoMessage: firstText(res?.promo_message, res?.message),
    walletBalance: num(res?.wallet_balance),
  };
}

// ─── invoices / cards ─────────────────────────────────────────────────────

const INVOICE_STATUSES: Readonly<Record<string, InvoiceStatus>> = {
  paid: 'paid',
  captured: 'paid',
  success: 'paid',
  succeeded: 'paid',
  completed: 'paid',
  pending: 'pending',
  initiated: 'pending',
  processing: 'pending',
  failed: 'failed',
  fail: 'failed',
  declined: 'failed',
  cancelled: 'failed',
  canceled: 'failed',
  refunded: 'refunded',
  refund: 'refunded',
};

/** Normalize a settlement status; anything unrecognised stays honest as `unknown`. */
export function toInvoiceStatus(value: string | null | undefined): InvoiceStatus {
  return INVOICE_STATUSES[String(value ?? '').trim().toLowerCase()] ?? 'unknown';
}

/** One `GET /invoices` row → domain {@link SubscriptionInvoice}; null when unusable. */
export function toInvoice(dto: InvoiceDto | null | undefined, index: number): SubscriptionInvoice | null {
  if (!dto || typeof dto !== 'object') return null;
  const id = dto.id != null ? String(dto.id) : firstText(dto.number, dto.invoice_number) ?? `inv-${index}`;
  return {
    id,
    number: firstText(dto.number, dto.invoice_number),
    issuedAt: toEpochMs(dto.issued_at ?? dto.paid_at ?? dto.created_at ?? dto.date),
    amount: firstNum(0, dto.amount, dto.total, dto.price),
    currency: firstText(dto.currency) ?? 'SAR',
    status: toInvoiceStatus(dto.status),
    url: firstText(dto.url, dto.invoice_url, dto.pdf_url, dto.receipt_url),
    planName: firstText(dto.plan_name) ?? (dto.plan ? toPlanName(dto.plan) || null : null),
    cycle: toCycleOrNull(dto.cycle) ?? (dto.plan ? toCycleOrNull(dto.plan.cycle) : null),
    automatic: String(dto.initiator ?? '').trim().toLowerCase() === 'mit',
  };
}

/** `GET /invoices` → newest first (the server's order is not guaranteed). */
export function toInvoices(list: readonly InvoiceDto[] | null | undefined): SubscriptionInvoice[] {
  if (!Array.isArray(list)) return [];
  return list
    .map(toInvoice)
    .filter((i): i is SubscriptionInvoice => i !== null)
    .sort((a, b) => b.issuedAt - a.issuedAt);
}

/** One `GET /cards` row → domain {@link SubscriptionCard}; null without an id. */
export function toCard(dto: CardDto | null | undefined, index: number): SubscriptionCard | null {
  if (!dto || typeof dto !== 'object') return null;
  const last4 = firstText(dto.last4, dto.last_four != null ? String(dto.last_four) : null);
  const id = dto.id != null ? String(dto.id) : last4 ? `card-${last4}` : `card-${index}`;
  return {
    id,
    brand: firstText(dto.brand, dto.scheme)?.toLowerCase() ?? null,
    last4,
    expMonth: num(dto.exp_month ?? dto.expiry_month),
    expYear: num(dto.exp_year ?? dto.expiry_year),
    isDefault: dto.is_default === true || dto.default === true,
  };
}

/** `GET /cards` → saved cards, the default one first. */
export function toCards(list: readonly CardDto[] | null | undefined): SubscriptionCard[] {
  if (!Array.isArray(list)) return [];
  return list
    .map(toCard)
    .filter((c): c is SubscriptionCard => c !== null)
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}
